import { z } from "zod";
import { adminSafeIdentifierSchema, adminUlidSchema } from "./admin-operations-core.js";
import { adminSupportWorkspaceSchemaVersionSchema } from "./admin-operations-support-workspace.js";
import { customerOperationsOpaqueIdSchema } from "./customer-operations-core.js";
import { bookSchema, bookMemberSchema } from "./schemas.js";
import { memberApprovedReaderBookSchema, studioExportSchema } from "./book-studio.js";
import { fulfillmentStateSchemaV1 } from "./fulfillment.js";
import { editionPagesResponseSchema } from "./edition-pages.js";
/** Same detail route; the only object locator accepted is the verified support request. */
export const adminSupportBookContextCommandSchema = z
    .object({
    schemaVersion: adminSupportWorkspaceSchemaVersionSchema,
    ticketId: customerOperationsOpaqueIdSchema,
    view: z.literal("BOOK_CONTEXT"),
})
    .strict();
const codeSchema = z.string().regex(/^[A-Z][A-Z0-9_]{0,119}$/u);
const evidenceSchema = z
    .object({
    id: adminSafeIdentifierSchema,
    source: z.enum(["STORY_JOB", "VARIANT_JOB", "PRINT_RENDER"]),
    task: codeSchema.nullable(),
    state: codeSchema,
    updatedAt: z.string().datetime(),
    failureCode: codeSchema.nullable(),
})
    .strict();
const orderSchema = z
    .object({
    physicalOrderId: adminUlidSchema,
    fulfillmentId: adminUlidSchema,
    state: fulfillmentStateSchemaV1,
    quantity: z.number().int().min(1).max(20),
    updatedAt: z.string().datetime(),
    test: z.boolean(),
})
    .strict();
const availableSchema = z
    .object({
    state: z.literal("AVAILABLE"),
    access: z
        .object({
        basis: z.literal("VERIFIED_SUPPORT_TICKET"),
        customerRole: bookMemberSchema.shape.role,
        readOnly: z.literal(true),
    })
        .strict(),
    book: bookSchema
        .pick({
        id: true,
        title: true,
        storytellerName: true,
        state: true,
        revision: true,
    })
        .extend({
        template: bookSchema.shape.template
            .pick({ id: true, name: true, workflowKind: true })
            .strict(),
    })
        .strict(),
    reader: z.discriminatedUnion("state", [
        z.object({ state: z.literal("AVAILABLE"), edition: memberApprovedReaderBookSchema }).strict(),
        z
            .object({
            state: z.literal("UNAVAILABLE"),
            reasonCode: z.enum(["NO_VERIFIED_READER_EDITION", "READER_LIMIT", "READER_CHANGING"]),
        })
            .strict(),
    ]),
    /** Exact retained native pages, ticket-bound. Absent on older service versions. */
    nativeReader: editionPagesResponseSchema.optional(),
    nativeReaderUnavailableReason: z.literal("REPORTED_EDITION_UNAVAILABLE").optional(),
    generation: z
        .object({
        records: z.array(evidenceSchema).max(100),
        truncated: z.boolean(),
        unreadableCount: z.number().int().nonnegative(),
    })
        .strict(),
    exports: z
        .object({
        records: z.array(studioExportSchema).max(50),
        truncated: z.boolean(),
        unreadableCount: z.number().int().nonnegative(),
    })
        .strict(),
    print: z
        .object({
        renders: z.array(evidenceSchema).max(50),
        truncated: z.boolean(),
        unreadableCount: z.number().int().nonnegative(),
        package: z
            .object({
            packageId: adminSafeIdentifierSchema,
            state: codeSchema,
            approvedPointer: z.boolean(),
        })
            .strict()
            .nullable(),
        order: z.discriminatedUnion("state", [
            z.object({ state: z.literal("AVAILABLE"), record: orderSchema }).strict(),
            z.object({ state: z.enum(["NOT_LINKED", "UNAVAILABLE"]) }).strict(),
        ]),
    })
        .strict(),
})
    .strict();
export const adminSupportBookContextSchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("NO_BOOK") }).strict(),
    availableSchema,
]);
export const adminSupportBookContextResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminSupportWorkspaceSchemaVersionSchema,
        ticketId: customerOperationsOpaqueIdSchema,
        context: adminSupportBookContextSchema,
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=admin-operations-support-book.js.map